import { generateMockPricePayload } from "./mockPrices";

export interface PriceEntry {
  date: string;
  price: number;
}

export interface PricePayload {
  today: PriceEntry[];
  tomorrow?: PriceEntry[];
}

export interface PriceData {
  today_60m: Record<string, number>;
  today_15m: Record<string, number>;
  tomorrow_60m?: Record<string, number>;
  tomorrow_15m?: Record<string, number>;
}

// Marks the second pass through a wall-clock hour when DST ends (e.g. "T02b:15"),
// so those quarters keep their own slots and still sort between 02:45 and 03:00.
export const REPEATED_HOUR_MARK = "b";

const isRepeatedLocalHour = (date: Date) => {
  const hourEarlier = new Date(date.getTime() - 60 * 60 * 1000);
  return (
    hourEarlier.getHours() === date.getHours() &&
    hourEarlier.getDate() === date.getDate()
  );
};

const toLocalHourKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const mark = isRepeatedLocalHour(date) ? REPEATED_HOUR_MARK : "";
  return `${year}-${month}-${day}T${hour}${mark}`;
};

const toIsoSlot = (date: Date) =>
  `${toLocalHourKey(date)}:${String(date.getMinutes()).padStart(2, "0")}`;

const hourKeyOfSlot = (slot: string) => slot.slice(0, slot.lastIndexOf(":"));

export const slotTimeLabel = (slot: string) =>
  (slot.split("T")[1] ?? slot).replace(REPEATED_HOUR_MARK, "");

const minutesBetween = (a: Date, b: Date) =>
  Math.abs(b.getTime() - a.getTime()) / (1000 * 60);

const roundToDecimals = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const normalisePrices = (entries: PriceEntry[]): PriceData => {
  const sorted = [...entries].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const today15m: Record<string, number> = {};

  const cadenceMinutes =
    sorted.length > 1
      ? minutesBetween(new Date(sorted[0].date), new Date(sorted[1].date))
      : 60;

  sorted.forEach((entry) => {
    const slot = toIsoSlot(new Date(entry.date));
    today15m[slot] = entry.price;
  });

  if (cadenceMinutes > 15) {
    // Hourly (or coarser) data, fan out to 15-minute slots with the same price.
    Object.entries(today15m).forEach(([slot, price]) => {
      const baseHour = hourKeyOfSlot(slot);
      ["00", "15", "30", "45"].forEach((quarter) => {
        today15m[`${baseHour}:${quarter}`] = price;
      });
      delete today15m[slot];
    });
  }

  // Always compute hourly prices as the average of the four 15m prices.
  const today60m: Record<string, number> = {};
  const buckets: Record<string, number[]> = {};
  Object.entries(today15m).forEach(([slot, price]) => {
    const hourKey = hourKeyOfSlot(slot);
    if (!buckets[hourKey]) {
      buckets[hourKey] = [];
    }
    buckets[hourKey].push(price);
  });

  Object.entries(buckets).forEach(([hourKey, prices]) => {
    const avg = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    today60m[`${hourKey}:00`] = roundToDecimals(avg, 6);
  });

  return { today_60m: today60m, today_15m: today15m };
};

const useMockInDev = import.meta.env.VITE_USE_MOCK_PRICES !== "false";
const includeMockTomorrow = import.meta.env.VITE_INCLUDE_MOCK_TOMORROW !== "false";

export async function fetchPriceData(): Promise<PriceData> {
  const payload =
    import.meta.env.DEV && useMockInDev
      ? generateMockPricePayload(includeMockTomorrow)
      : await (async () => {
          const response = await fetch("/api/prices");
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          return (await response.json()) as PricePayload;
        })();

  if (!payload?.today || !Array.isArray(payload.today)) {
    throw new Error("API response missing today prices");
  }

  const today = normalisePrices(payload.today);
  const tomorrow =
    payload.tomorrow && Array.isArray(payload.tomorrow) && payload.tomorrow.length > 0
      ? normalisePrices(payload.tomorrow)
      : undefined;

  return {
    ...today,
    ...(tomorrow
      ? {
          tomorrow_60m: tomorrow.today_60m,
          tomorrow_15m: tomorrow.today_15m,
        }
      : {}),
  };
}

export function isTomorrowDataAvailable(): boolean {
  const now = new Date();
  return now.getHours() >= 14;
}

export function getCurrentTimeSlot(
  interval: "60m" | "15m",
  now: Date = new Date()
): string {
  const hourKey = toLocalHourKey(now);

  if (interval === "60m") {
    return `${hourKey}:00`;
  }

  const minutes = Math.floor(now.getMinutes() / 15) * 15;
  const minute = String(minutes).padStart(2, "0");
  return `${hourKey}:${minute}`;
}

